import { apiGet, apiPost } from "../lib/api-client";

// ─── Types (mirror the backend contract — do not invent fields) ──────────────

export type StoreItemKind =
  | "badge"
  | "banner"
  | "title"
  | "map_theme"
  | "award"
  | "physical"
  | "bundle"
  | "frame"
  | "trace_style"
  | "stamp"
  | "pin";

export type StoreItemRarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

export interface StoreItemRequirement {
  type: "achievement" | "collection" | "trail" | "region";
  slug: string;
  /** region only — coverage percentage needed (defaults to 75 server-side). */
  minCoveragePct?: number;
}

export interface StoreItemMetadata {
  // badge / banner / frame / stamp
  assetKey?: string;
  // title
  text?: string;
  colorToken?: string;
  /** #RRGGBB fallback for tokens this build predates. */
  colorHex?: string;
  // map_theme
  heatRamp?: [string, string, string, string, string];
  traceColor?: string;
  // trace_style
  dashArray?: number[];
  glow?: true;
  // pin
  color?: string;
  // award
  pointsToRecipient?: number;
  icon?: string;
  packSize?: number;
  // physical
  requiresShipping?: boolean;
  supplier?: string | null;
  weightG?: number | null;
  printfulProductId?: string | null;
  // bundle
  grantsItemSlugs?: string[];
  grantsLifetimePro?: true;
  /** Waypoints gift pack — pence-only, sent to another explorer at checkout. */
  grantsWaypoints?: number;
}

export interface StoreItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  kind: StoreItemKind;
  priceWaypoints: number | null;
  pricePence: number | null;
  requirement: StoreItemRequirement | null;
  imageUrl: string | null;
  metadata: StoreItemMetadata;
  stock: number | null;
  /** Set while a seasonal window is open — "leaves the store" copy. */
  availableUntil: string | null;
  featured: boolean;
  sortOrder: number;
  rarity: StoreItemRarity;
  locked: boolean;
  lockedReason: string | null;
  owned: boolean;
  ownedQuantity: number;
}

export interface StoreItemsResponse {
  items: StoreItem[];
}

export interface WalletLedgerEntry {
  id: string;
  delta: number;
  reason: string;
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

export interface WalletResponse {
  waypoints: number;
  recent: WalletLedgerEntry[];
}

export interface InventoryItem {
  id: string;
  itemId: string;
  slug: string;
  name: string;
  kind: StoreItemKind;
  metadata: StoreItemMetadata;
  rarity: StoreItemRarity;
  quantity: number;
  acquiredVia: "waypoints" | "purchase" | "bundle" | "grant";
  createdAt: string;
}

export interface InventoryEquipped {
  badgeItemIds: string[];
  bannerItemId: string | null;
  titleItemId: string | null;
  mapThemeItemId: string | null;
  frameItemId: string | null;
  traceStyleItemId: string | null;
  stampItemId: string | null;
  pinItemId: string | null;
}

export interface InventoryResponse {
  items: InventoryItem[];
  equipped: InventoryEquipped;
}

export interface PurchaseResponse {
  order: {
    id: string;
    status: string;
    totalWaypoints: number | null;
  };
  waypoints: number;
}

export type EquipSlot =
  | "badges"
  | "banner"
  | "title"
  | "mapTheme"
  | "frame"
  | "traceStyle"
  | "stamp"
  | "pin";

export interface EquipResponse {
  equipped: InventoryEquipped;
}

export interface CheckoutResponse {
  url: string;
}

// ─── Fetch functions ──────────────────────────────────────────────────────────

export function getWallet(): Promise<WalletResponse> {
  return apiGet("/economy/wallet");
}

export function getStoreItems(): Promise<StoreItemsResponse> {
  return apiGet("/store/items");
}

export function getInventory(): Promise<InventoryResponse> {
  return apiGet("/store/inventory");
}

export function purchaseItem(data: {
  itemSlug: string;
  currency: "waypoints";
  quantity?: number;
}): Promise<PurchaseResponse> {
  return apiPost("/store/purchase", data);
}

export function equipItems(data: {
  slot: EquipSlot;
  itemIds: string[];
}): Promise<EquipResponse> {
  return apiPost("/store/equip", data);
}

export function createStoreCheckout(data: {
  items: { slug: string; quantity?: number }[];
  /** Required when buying a Waypoints gift pack — the recipient's username. */
  giftToUsername?: string;
  giftMessage?: string;
}): Promise<CheckoutResponse> {
  return apiPost("/store/checkout", data);
}
